/* eslint-disable no-console */
import { AnalyticsProvider, IdentityPayload, TrackProps } from '../manager';

declare global {
  interface Window {
    _hsq: any[][];
  }
}

export class HubSpotProvider implements AnalyticsProvider {
  private isInitialized = false;

  /**
   * Initializes the HubSpot provider
   * Unlike other providers, HubSpot is loaded via a script tag in the HTML and initialized asynchronously
   * We set isInitialized=true here but still check for window._hsq in isReady() since the script may not be fully loaded
   */
  public init(options: { debug?: boolean }): void {
    this.isInitialized = true;
    if (options.debug) console.debug('HubSpot initialized successfully');
  }

  public identify(identity: IdentityPayload): void {
    if (!this.isReady()) return;
    try {
      window._hsq.push([
        'identify',
        {
          id: identity.userId,
          email: identity.email,
          firstname: identity.firstName,
          lastname: identity.lastName,
          ...identity.properties,
        },
      ]);

      // For HubSpot, we need to set the path and track the page view manually or we won't be able to identify the user
      const path = window.location.pathname + window.location.search;
      window._hsq.push(['setPath', path]);
      window._hsq.push(['trackPageView']);
    } catch (error) {
      console.debug('HubSpot identify failed:', error);
    }
  }

  public track(event: string, properties?: TrackProps): void {
    if (!this.isReady()) return;
    try {
      // `trackEvent` requires the Marketing Hub Enterprise plan
      // Fourwaves is currently on the Marketing Hub Professional plan, so we can't use it
      // The implementation below will work only if we upgrade.
      window._hsq.push([
        'trackEvent',
        {
          id: event,
          value: properties,
        },
      ]);

      // Also track page views
      if (event === 'page_viewed') window._hsq.push(['trackPageView']);
    } catch (error) {
      console.debug('HubSpot track failed:', error);
    }
  }

  public isReady(): boolean {
    return this.isInitialized && !!window._hsq;
  }

  public startSessionRecording(): void {
    // No-op: Session recording is not needed for HubSpot
  }

  public stopSessionRecording(): void {
    // No-op: Session recording is not needed for HubSpot
  }
}
